import React from 'react';
import { Trophy, Trash2, Clock, Award } from 'lucide-react';
import { SpinHistory } from '../../types';

interface WinnerHistoryListProps {
  history: SpinHistory[];
  onDeleteHistoryItem: (id: string) => void;
  onClearHistory: () => void;
  disabled?: boolean;
}

export const WinnerHistoryList: React.FC<WinnerHistoryListProps> = ({
  history,
  onDeleteHistoryItem,
  onClearHistory,
  disabled = false,
}) => {
  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0.75rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Trophy size={16} color="#f59e0b" />
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            Total Winners: {history.length}
          </span>
        </div>

        {history.length > 0 && (
          <button
            onClick={() => {
              if (window.confirm('Clear all winner history for this wheel?')) {
                onClearHistory();
              }
            }}
            disabled={disabled}
            className="btn-ghost"
            style={{ fontSize: '0.75rem', padding: '2px 6px', color: '#ef4444' }}
          >
            <Trash2 size={13} />
            Clear
          </button>
        )}
      </div>

      {history.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
            gap: '8px',
          }}
        >
          <Award size={36} style={{ opacity: 0.4 }} />
          <p style={{ fontSize: '0.9rem', fontWeight: 600 }}>No winners recorded yet</p>
          <p style={{ fontSize: '0.8rem', opacity: 0.8 }}>Spin the wheel to crown your first champion!</p>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            paddingRight: '4px',
            maxHeight: '420px',
          }}
        >
          {history.map((item, index) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.55rem 0.75rem',
                background: 'var(--bg-surface-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: index === 0 ? '#f59e0b' : 'var(--text-muted)',
                    minWidth: '20px',
                  }}
                >
                  #{history.length - index}
                </span>

                <div style={{ overflow: 'hidden' }}>
                  <div
                    style={{
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: index === 0 ? '#f59e0b' : 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.winnerLabel}
                  </div>
                  <div
                    style={{
                      fontSize: '0.7rem',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Clock size={11} />
                    <span>
                      {formatTime(item.timestamp)} ({formatDate(item.timestamp)})
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => onDeleteHistoryItem(item.id)}
                disabled={disabled}
                className="btn-ghost"
                style={{ padding: '4px', color: 'var(--text-muted)' }}
                title="Remove item"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
